import { useState } from 'react';
import {
  Box,
  Card,
  Typography,
  Avatar,
  Button,
  Chip,
  LinearProgress,
  Tooltip,
  Grid,
  Divider
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import { base_logo_url } from '../../apis/constants';

// ─── avatar color derived from carrier name ───────────────────────────────────
const AVATAR_COLORS = [
  '#5048c8',
  '#764ba2',
  '#1d9e75',
  '#d85a30',
  '#185fa5',
  '#854f0b',
  '#993556',
  '#3b6d11'
];
const getAvatarColor = (name = '') => {
  let hash = 0;
  for (let i = 0; i < name.length; i++)
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};
const getInitials = (name = '') =>
  name
    .trim()
    .split(/\s+/)
    .map(w => w[0])
    .slice(0, 2)
    .join('')
    .toUpperCase() || '?';

// ─── helpers ──────────────────────────────────────────────────────────────────

const SectionLabel = ({ children }) => (
  <Typography
    sx={{
      fontSize: '0.68rem',
      fontWeight: 600,
      letterSpacing: '0.06em',
      textTransform: 'uppercase',
      color: 'text.secondary',
      mb: 1
    }}
  >
    {children}
  </Typography>
);

const InfoRow = ({ label, children, noBorder }) => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: 1,
      py: 0.6,
      borderBottom: noBorder ? 'none' : '1px solid',
      borderColor: 'divider',
      '&:last-of-type': { borderBottom: 'none', pb: 0 }
    }}
  >
    <Typography
      sx={{ fontSize: '0.75rem', color: 'text.secondary', flexShrink: 0 }}
    >
      {label}
    </Typography>
    {children}
  </Box>
);

const CopyableValue = ({ text, label }) => {
  const [copied, setCopied] = useState(false);
  if (!text || text === '-')
    return <Typography sx={{ fontSize: '0.75rem' }}>—</Typography>;
  const handleCopy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };
  return (
    <Tooltip title={copied ? 'Copied!' : `Copy ${label}`} placement='top'>
      <Box
        onClick={handleCopy}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.4,
          cursor: 'pointer',
          '&:hover .copy-icon': { opacity: 1 }
        }}
      >
        <Typography
          sx={{ fontSize: '0.75rem', fontWeight: 500, textAlign: 'right' }}
        >
          {text}
        </Typography>
        {copied ? (
          <CheckIcon sx={{ fontSize: '0.7rem', color: 'success.main' }} />
        ) : (
          <ContentCopyIcon
            className='copy-icon'
            sx={{
              fontSize: '0.7rem',
              color: 'text.disabled',
              opacity: 0.5,
              transition: 'opacity .15s'
            }}
          />
        )}
      </Box>
    </Tooltip>
  );
};

const SmallChip = ({ label, color = 'default' }) => (
  <Chip
    label={label}
    size='small'
    color={color}
    sx={{ height: 20, fontSize: '0.65rem', '& .MuiChip-label': { px: 0.8 } }}
  />
);

const StatBox = ({ label, value, valueColor }) => (
  <Box
    sx={{
      textAlign: 'center',
      p: 0.75,
      bgcolor: 'background.paper',
      borderRadius: 1,
      border: '1px solid',
      borderColor: 'divider'
    }}
  >
    <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>
      {label}
    </Typography>
    <Typography
      sx={{
        fontSize: '1rem',
        fontWeight: 600,
        color: valueColor || 'text.primary',
        mt: 0.2
      }}
    >
      {value || '—'}
    </Typography>
  </Box>
);

const Section = ({ title, children, sx }) => (
  <Box
    sx={{
      bgcolor: 'grey.50',
      borderRadius: 1.5,
      p: 1.5,
      border: '1px solid',
      borderColor: 'divider',
      ...sx
    }}
  >
    <SectionLabel>{title}</SectionLabel>
    {children}
  </Box>
);

// Empty data component for sections without data
const EmptySectionData = () => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      py: 3,
      color: 'text.disabled'
    }}
  >
    <Typography sx={{ fontSize: '0.75rem', fontStyle: 'italic' }}>
      No data available
    </Typography>
  </Box>
);

// ─── main component ───────────────────────────────────────────────────────────

const CarrierReadOnlyView = ({ carrier = {}, onEdit }) => {
  const calc = (total, left) => {
    const t = Number(total) || 0;
    const l = Number(left) || 0;
    return isNaN(t) || isNaN(l) ? 0 : t - l;
  };

  const totalEpro = carrier.proNumberTotals || 0;
  const consumedEpro = calc(carrier.proNumberTotals, carrier.proNumberLeft);
  const remainingEpro = carrier.proNumberLeft || 0;
  const progress =
    totalEpro > 0 ? Math.round((consumedEpro / totalEpro) * 100) : 0;
  const alertLow =
    remainingEpro > 0 && remainingEpro < carrier.proNumberAlertOn;

  const uniq = arr => [...new Set(arr)];

  // Fixed: Better handling of shipment types
  const getShipmentTypes = () => {
    const types = [];

    // Check rqSettingType
    if (carrier.rqSettingType === 'Both') {
      types.push('Inbound', 'Outbound');
    } else if (carrier.rqSettingType === 'Inbound') {
      types.push('Inbound');
    } else if (carrier.rqSettingType === 'OutBound') {
      types.push('Outbound');
    }

    // Check rqSettingType2
    if (carrier.rqSettingType2 === 'Both') {
      types.push('Inbound', 'Outbound');
    } else if (carrier.rqSettingType2 === 'Inbound') {
      types.push('Inbound');
    } else if (carrier.rqSettingType2 === 'OutBound') {
      types.push('Outbound');
    }

    return uniq(types);
  };

  // Fixed: Better handling of rate types
  const getRateTypes = () => {
    const types = [];

    // Check rqSettingTypeInfo
    if (carrier.rqSettingTypeInfo === 'All') {
      types.push('Standard', 'Guaranteed');
    } else if (carrier.rqSettingTypeInfo === 'Standard') {
      types.push('Standard');
    } else if (carrier.rqSettingTypeInfo === 'Guaranteed') {
      types.push('Guaranteed');
    }

    // Check rqSettingTypeInfo2
    if (carrier.rqSettingTypeInfo2 === 'All') {
      types.push('Standard', 'Guaranteed');
    } else if (carrier.rqSettingTypeInfo2 === 'Standard') {
      types.push('Standard');
    } else if (carrier.rqSettingTypeInfo2 === 'Guaranteed') {
      types.push('Guaranteed');
    }

    return uniq(types);
  };

  // New function to handle filter types
  const getFilterTypes = () => {
    const types = [];

    // Check rqSettingTypeFilter
    if (carrier.rqSettingTypeFilter === 'Quickest') {
      types.push('Quickest');
    } else if (carrier.rqSettingTypeFilter === 'All') {
      types.push('All');
    }

    // Check rqSettingTypeFilter2
    if (carrier.rqSettingTypeFilter2 === 'Quickest') {
      types.push('Quickest');
    } else if (carrier.rqSettingTypeFilter2 === 'All') {
      types.push('All');
    }

    return uniq(types);
  };

  const getSurchargeApplyOn = () =>
    carrier.surchargeApplyOn?.split(',').filter(Boolean) || [];

  const getFullAddress = () =>
    [
      carrier.tpStreetLines,
      carrier.tpCity,
      carrier.tpStateOrProvinceCode,
      carrier.tpPostalCode,
      carrier.tpCountryCode
    ]
      .filter(Boolean)
      .join(', ') || null;

  const hasThirdParty =
    carrier.thirdPartyAccountNumber || carrier.tpCompanyName || carrier.tpEmail;
  const hasSurcharge = carrier.surcharge > 0;
  const hasThreshold = carrier.iaThreshold > 0;
  const hasProSeries = !!(carrier.proNumberStart && carrier.proNumberEnd);
  const hasClaimContact = !!carrier.claimContactPerson;

  const avatarBg = getAvatarColor(carrier.name);
  const initials = getInitials(carrier.name);

  return (
    <Card
      elevation={0}
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2.5,
        overflow: 'hidden',
        height: '100%',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      {/* ── Header ── */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #5048c8 0%, #764ba2 100%)',
          px: 2,
          py: 1.4,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar
            src={carrier.logo ? base_logo_url + carrier.logo : undefined}
            alt={carrier.name}
            variant='rounded'
            sx={{
              width: 38,
              height: 38,
              bgcolor: avatarBg,
              border: '2px solid rgba(255,255,255,0.35)',
              fontSize: '0.8rem',
              fontWeight: 700,
              color: 'white',
              '& img': {
                objectFit: 'contain',
                backgroundColor: 'white',
                padding: '3px',
                borderRadius: '4px',
                width: '100%',
                height: '100%'
              }
            }}
          >
            {initials}
          </Avatar>
          <Box>
            <Typography
              sx={{
                color: 'white',
                fontWeight: 600,
                fontSize: '0.95rem',
                lineHeight: 1.2
              }}
            >
              {carrier.name || 'Carrier'}
            </Typography>
            <Typography
              sx={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.7rem' }}
            >
              {carrier.contactEmail?.split(',')[0] || ''}
            </Typography>
          </Box>
        </Box>
        <Button
          onClick={onEdit}
          size='small'
          sx={{
            textTransform: 'none',
            bgcolor: 'rgba(255,255,255,0.15)',
            color: 'white',
            fontSize: '0.72rem',
            px: 1.5,
            py: 0.5,
            borderRadius: 1,
            border: '1px solid rgba(255,255,255,0.3)',
            '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' }
          }}
        >
          Edit Settings
        </Button>
      </Box>

      {/* ── Body ── */}
      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          p: 1.5,
          bgcolor: 'background.default'
        }}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: 1.5,
            alignItems: 'stretch'
          }}
        >
          {/* Row 1 Left — E-PRO Allocation & Pro Number Series */}
          <Section title='E-PRO Allocation & Pro Number Series'>
            <Grid container spacing={0.75} sx={{ mb: 1 }}>
              <Grid item xs={4}>
                <StatBox
                  label='Total'
                  value={totalEpro > 0 ? totalEpro : '—'}
                />
              </Grid>
              <Grid item xs={4}>
                <StatBox
                  label='Consumed'
                  value={consumedEpro > 0 ? consumedEpro : '—'}
                />
              </Grid>
              <Grid item xs={4}>
                <StatBox
                  label='Remaining'
                  value={remainingEpro > 0 ? remainingEpro : '—'}
                  valueColor={alertLow ? 'error.main' : 'success.main'}
                />
              </Grid>
            </Grid>
            <Box sx={{ mb: 0.5 }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  mb: 0.4
                }}
              >
                <Typography
                  sx={{ fontSize: '0.68rem', color: 'text.secondary' }}
                >
                  Usage
                </Typography>
                <Typography sx={{ fontSize: '0.68rem', fontWeight: 600 }}>
                  {progress}%
                </Typography>
              </Box>
              <LinearProgress
                variant='determinate'
                value={progress}
                sx={{
                  height: 5,
                  borderRadius: 3,
                  bgcolor: 'grey.200',
                  '& .MuiLinearProgress-bar': {
                    bgcolor:
                      progress > 80
                        ? 'error.main'
                        : progress > 60
                          ? 'warning.main'
                          : 'primary.main'
                  }
                }}
              />
            </Box>
            <InfoRow label='Alert level'>
              <Typography sx={{ fontSize: '0.75rem', fontWeight: 500 }}>
                {carrier.proNumberAlertOn || '—'}
              </Typography>
            </InfoRow>
            {carrier.proNumberAlertNotifyTo && (
              <InfoRow label='Alert email' noBorder>
                <CopyableValue
                  text={carrier.proNumberAlertNotifyTo}
                  label='Alert Email'
                />
              </InfoRow>
            )}
            {hasProSeries && (
              <Box>
                <InfoRow label='Range'>
                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 500 }}>
                    {carrier.proNumberStart} – {carrier.proNumberEnd}
                  </Typography>
                </InfoRow>
                {carrier.proNumberSuffix && (
                  <InfoRow
                    label={`Suffix (${carrier.proNumberSuffixPosition || 'Pre'})`}
                    noBorder
                  >
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 500 }}>
                      {carrier.proNumberSuffix}
                    </Typography>
                  </InfoRow>
                )}
              </Box>
            )}
          </Section>

          {/* Row 1 Right — Third-Party Account */}
          <Section title='Third-Party Account'>
            {hasThirdParty ? (
              <>
                {carrier.thirdPartyAccountNumber && (
                  <InfoRow label='Account #'>
                    <CopyableValue
                      text={carrier.thirdPartyAccountNumber}
                      label='Account Number'
                    />
                  </InfoRow>
                )}
                {carrier.tpCompanyName && (
                  <InfoRow label='Company'>
                    <Typography
                      sx={{
                        fontSize: '0.75rem',
                        fontWeight: 500,
                        textAlign: 'right'
                      }}
                    >
                      {carrier.tpCompanyName}
                    </Typography>
                  </InfoRow>
                )}
                {getFullAddress() && (
                  <InfoRow label='Address'>
                    <Typography
                      sx={{
                        fontSize: '0.75rem',
                        textAlign: 'right',
                        maxWidth: '62%'
                      }}
                    >
                      {getFullAddress()}
                    </Typography>
                  </InfoRow>
                )}
                {carrier.tpPhoneNumber && (
                  <InfoRow label='Phone'>
                    <CopyableValue text={carrier.tpPhoneNumber} label='Phone' />
                  </InfoRow>
                )}
                {carrier.tpEmail && (
                  <InfoRow label='Email' noBorder>
                    <CopyableValue text={carrier.tpEmail} label='Email' />
                  </InfoRow>
                )}
              </>
            ) : (
              <EmptySectionData />
            )}
          </Section>

          {/* Row 2 Left — Rate Configuration */}
          <Section title='Rate Configuration'>
            <InfoRow label='Shipment Types' noBorder>
              <Box
                sx={{
                  display: 'flex',
                  gap: 0.5,
                  flexWrap: 'wrap',
                  justifyContent: 'flex-end'
                }}
              >
                {['Inbound', 'Outbound'].map(type => {
                  const isActive = getShipmentTypes().includes(type);
                  return (
                    <Chip
                      key={type}
                      label={type}
                      size='small'
                      color={isActive ? 'success' : 'default'}
                      sx={{
                        height: 20,
                        fontSize: '0.65rem',
                        '& .MuiChip-label': { px: 0.8 },
                        opacity: isActive ? 1 : 0.5,
                        backgroundColor: isActive ? undefined : 'grey.300'
                      }}
                    />
                  );
                })}
              </Box>
            </InfoRow>
            <Divider sx={{ my: 0 }} />
            <InfoRow label='Rate Types'>
              <Box
                sx={{
                  display: 'flex',
                  gap: 0.5,
                  flexWrap: 'wrap',
                  justifyContent: 'flex-end'
                }}
              >
                {['Standard', 'Guaranteed'].map(type => {
                  const isActive = getRateTypes().includes(type);
                  return (
                    <Chip
                      key={type}
                      label={type}
                      size='small'
                      color={isActive ? 'primary' : 'default'}
                      sx={{
                        height: 20,
                        fontSize: '0.65rem',
                        '& .MuiChip-label': { px: 0.8 },
                        opacity: isActive ? 1 : 0.5,
                        backgroundColor: isActive ? undefined : 'grey.300'
                      }}
                    />
                  );
                })}
              </Box>
            </InfoRow>
            <InfoRow label='Guaranteed Types' noBorder>
              <Box
                sx={{
                  display: 'flex',
                  gap: 0.5,
                  flexWrap: 'wrap',
                  justifyContent: 'flex-end'
                }}
              >
                {['Quickest', 'All'].map(type => {
                  const isActive = getFilterTypes().includes(type);
                  return (
                    <Chip
                      key={type}
                      label={type}
                      size='small'
                      color={isActive ? 'warning' : 'default'}
                      sx={{
                        height: 20,
                        fontSize: '0.65rem',
                        '& .MuiChip-label': { px: 0.8 },
                        opacity: isActive ? 1 : 0.5,
                        backgroundColor: isActive ? undefined : 'grey.300'
                      }}
                    />
                  );
                })}
              </Box>
            </InfoRow>
          </Section>

          {/* Row 2 Right — Claim Contact or Pro Number Series */}
          <Section title={'Claim Contact'}>
            {hasClaimContact ? (
              <>
                <InfoRow label='Name'>
                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 500 }}>
                    {carrier.claimContactPerson}
                  </Typography>
                </InfoRow>
                {carrier.claimContactEmail && (
                  <InfoRow label='Email'>
                    <CopyableValue
                      text={carrier.claimContactEmail}
                      label='Claim Email'
                    />
                  </InfoRow>
                )}
                {carrier.claimContactPhone && (
                  <InfoRow label='Phone'>
                    <CopyableValue
                      text={carrier.claimContactPhone}
                      label='Claim Phone'
                    />
                  </InfoRow>
                )}
                {carrier.claimContactCC && (
                  <InfoRow label='Copy to' noBorder>
                    <CopyableValue
                      text={carrier.claimContactCC}
                      label='CC Email'
                    />
                  </InfoRow>
                )}
              </>
            ) : (
              <EmptySectionData />
            )}
          </Section>

          {/* Row 3 Left — Surcharge */}
          <Section title='Surcharge'>
            {hasSurcharge ? (
              <>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'baseline',
                    gap: 0.75,
                    mb: getSurchargeApplyOn().length > 0 ? 0.75 : 0
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '1.3rem',
                      fontWeight: 700,
                      color: 'warning.dark'
                    }}
                  >
                    {carrier.surcharge}
                    {carrier.surchargeType || '$'}
                  </Typography>
                  <Typography
                    sx={{ fontSize: '0.7rem', color: 'text.secondary' }}
                  >
                    per shipment
                  </Typography>
                </Box>
                {getSurchargeApplyOn().length > 0 && (
                  <InfoRow label='Apply on' noBorder>
                    <Box
                      sx={{
                        display: 'flex',
                        gap: 0.5,
                        flexWrap: 'wrap',
                        justifyContent: 'flex-end'
                      }}
                    >
                      {getSurchargeApplyOn().map(t => (
                        <SmallChip key={t} label={t} />
                      ))}
                    </Box>
                  </InfoRow>
                )}
              </>
            ) : (
              <EmptySectionData />
            )}
          </Section>

          {/* Row 3 Right — Invoice Threshold */}
          <Section title='Invoice Threshold'>
            {hasThreshold ? (
              <InfoRow label='Threshold' noBorder>
                <Typography
                  sx={{
                    fontSize: '0.9rem',
                    fontWeight: 700,
                    color: 'error.main'
                  }}
                >
                  {carrier.iaThreshold}
                  {carrier.iaThresholdType || '$'}
                </Typography>
              </InfoRow>
            ) : (
              <EmptySectionData />
            )}
          </Section>
        </Box>
      </Box>
    </Card>
  );
};

export default CarrierReadOnlyView;
